import { useEffect, useState } from 'react';
import type { AuditPayload, AuditProgram, AuditType } from './types';

interface AuditFormModalProps {
  open: boolean;
  programs: AuditProgram[];
  onClose: () => void;
  onSubmit: (payload: AuditPayload) => Promise<void>;
}

const TYPES: AuditType[] = ['interna', 'externa'];

export function AuditFormModal({ open, programs, onClose, onSubmit }: AuditFormModalProps) {
  const [programId, setProgramId] = useState('');
  const [type, setType] = useState<AuditType>('interna');
  const [scope, setScope] = useState('');
  const [criteria, setCriteria] = useState('ISO 9001:2015');
  const [auditeeArea, setAuditeeArea] = useState('');
  const [externalEntity, setExternalEntity] = useState('');
  const [plannedDate, setPlannedDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (programs.length > 0 && !programId) {
      setProgramId(programs[0].id);
    }
  }, [open, programs, programId]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!scope.trim() || !plannedDate) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        programId: programId || null,
        type,
        scope: scope.trim(),
        criteria: criteria.trim() || 'ISO 9001:2015',
        auditeeArea: auditeeArea.trim() || null,
        externalEntity: type === 'externa' ? externalEntity.trim() || null : null,
        plannedDate: new Date(plannedDate).toISOString(),
      });
      onClose();
      setScope('');
      setAuditeeArea('');
      setExternalEntity('');
    } catch {
      setError('No se pudo crear la auditoría.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-lg p-6 space-y-4">
        <h2 className="text-lg font-semibold">Nueva auditoría</h2>
        <form className="space-y-3" onSubmit={(e) => void handleSubmit(e)}>
          {programs.length > 0 && (
            <label className="block text-sm">
              Programa
              <select
                className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
                value={programId}
                onChange={(e) => setProgramId(e.target.value)}
              >
                {programs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.year}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="block text-sm">
            Tipo
            <select
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={type}
              onChange={(e) => setType(e.target.value as AuditType)}
            >
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            Alcance
            <textarea
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm min-h-[80px]"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              required
              placeholder="Procesos y áreas incluidas en la auditoría…"
            />
          </label>
          <label className="block text-sm">
            Criterios / normas
            <input
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={criteria}
              onChange={(e) => setCriteria(e.target.value)}
              placeholder="Ej: ISO 9001:2015"
            />
          </label>
          <label className="block text-sm">
            Área auditada
            <input
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={auditeeArea}
              onChange={(e) => setAuditeeArea(e.target.value)}
              placeholder="Ej: Almacén, Producción…"
            />
          </label>
          {type === 'externa' && (
            <label className="block text-sm">
              Entidad externa
              <input
                className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
                value={externalEntity}
                onChange={(e) => setExternalEntity(e.target.value)}
                placeholder="Ej: Organismo certificador"
              />
            </label>
          )}
          <label className="block text-sm">
            Fecha planificada
            <input
              type="date"
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={plannedDate}
              onChange={(e) => setPlannedDate(e.target.value)}
              required
            />
          </label>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Guardando…' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
